import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { DestinationPickerPopover } from 'components/DestinationPickerPopover';
import { isEqual } from 'date-fns';
import { OnDestinationChangeParams, OpenedFormControl, RecentSearchParams } from 'hooks/searchBox';
import { useDatePickerTiming } from 'hooks/useDatePickerTiming';
import { getAnalyticsPageName, getMeta } from 'modules/meta/selectors';
import { getAnchorComplete, getHotelIdsForPriceColorGuideCalendars, getNumberOfGuests, getSearchParameters, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { SearchType } from 'modules/sapiSearch/utils';
import { getIsMultiRoomBundlesSearch, getIsUserLandingDirectlyOnSrp, getIsUserSearch, isDatesDefault } from 'modules/search/selectors';
import { getIsSearchBoxActive, getOpenDatePickerType, getSearchBoxSearchType, getSearchBoxValues, getShowRoomsLabel } from 'modules/searchBox/selectors';
import { setIsSearchBoxActive } from 'modules/searchBox/slice';
import { toggle } from 'opticks';
import { PopoverPlacement } from '@daedalus/atlas/Popover';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity, Page, TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import { DatePickerType } from '@daedalus/core/src/datePicker/types';
import { ConfigureI18nContext } from '@daedalus/core/src/localization/components/ConfigureI18n';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { roomsSplitToString } from '@daedalus/core/src/room/business/roomConfiguration';
import { RoomConfigType, RoomSplitType } from '@daedalus/core/src/room/types/RoomConfiguration';
import { WithColorPriceDayData } from '@daedalus/core/src/search/modules/ColorPriceCalendar/components/WithColorPriceDayData';
import { useColorPriceCalendar } from '@daedalus/core/src/search/modules/ColorPriceCalendar/hooks';
import { getHasSomeAvailabilityPrices } from '@daedalus/core/src/search/modules/ColorPriceCalendar/selectors';
import { dateStringToMiddayDate } from '@daedalus/core/src/utils/date';
import { POPOVER_HORIZONTAL_OFFSET, POPOVER_VERTICAL_OFFSET } from '@daedalus/shared/src/search/constants';
import { DatePickerPopover } from '@daedalus/shared/src/search/DatePickerPopover';
import { GuestRoomConfigPopover } from '@daedalus/shared/src/search/GuestRoomConfig/GuestRoomConfigPopover';
import { SearchFormHorizontal } from '@daedalus/shared/src/search/SearchFormHorizontal';
import { KiwiSearchForm } from '@daedalus/shared/src/whiteLabel/KiwiSearchForm/KiwiSearchForm';
import { SearchFormHorizontalFullWidth } from '@daedalus/shared/src/whiteLabel/SearchFormHorizontalFullWidth';
interface Props {
  roomsSplit: RoomSplitType[];
  initialCheckIn: string;
  initialCheckOut: string;
  initialRooms: string;
  initialOptimizeRooms: BooleanUrlParam;
  checkIn: string;
  checkOut: string;
  destinationDisplayValue: string;
  destinationPickerInputDisplayValue: string;
  numberOfRooms: number;
  numberOfAdults: number;
  numberOfChildren: number;
  optimizeRooms?: boolean;
  onDatePickerInputClick: (name: string) => void;
  onDestinationPickerInputClick: () => void;
  onGuestPickerClick: () => void;
  onDestinationChange: (params: OnDestinationChangeParams) => void;
  onSuggestionSelected: () => void;
  onRecentSearchSelected: (params: RecentSearchParams) => void;
  onSuggestionsRequested: (value: string, shouldLoadUserSearchHistory: boolean) => void;
  onDateChange: (checkInDate: string, checkOutDate: string) => void;
  onDatePickerOpen: (type: string) => void;
  onDatePickerDayClick: (name: string, value: string) => void;
  onSubmit: (event?: React.SyntheticEvent) => void | boolean;
  onGuestRoomsChange: (roomSplit: RoomSplitType[]) => void;
  onGuestOptimizeRoomsChange: (isOptimizeRooms?: boolean) => void;
  onGuestPickerReset: () => void;
  onDatePickerClose: () => void;
  onDatePickerReset: () => void;
  onNearbyPropertiesClick: () => void;
  onGuestPickerInfoDisplayed: () => void;
  onGuestConfigPreferenceClick: (selectedOption: RoomConfigType) => void;
}
const FormContainer = /*#__PURE__*/styled('div')({
  name: "FormContainer",
  class: "fldhkfl",
  propsAsIs: false
});

/**
 * The SearchFormDesktop component is the search form in the middle of the top navigation part of the Search page.
 * This search form is used for desktop layouts.
 */
export const SearchFormDesktop = ({
  initialCheckIn,
  initialCheckOut,
  initialRooms,
  initialOptimizeRooms,
  checkIn,
  checkOut,
  destinationDisplayValue,
  destinationPickerInputDisplayValue,
  numberOfAdults,
  numberOfChildren,
  numberOfRooms,
  optimizeRooms,
  onDatePickerInputClick,
  onDestinationPickerInputClick,
  onGuestPickerClick,
  onSuggestionSelected,
  onRecentSearchSelected,
  onDestinationChange,
  onSuggestionsRequested,
  onDateChange,
  onDatePickerOpen,
  onDatePickerDayClick,
  onSubmit,
  onGuestRoomsChange,
  onGuestOptimizeRoomsChange,
  onGuestPickerReset,
  onDatePickerClose,
  onDatePickerReset,
  onNearbyPropertiesClick,
  onGuestPickerInfoDisplayed,
  onGuestConfigPreferenceClick,
  roomsSplit
}: Props) => {
  const dispatch = useDispatch();
  const isSearchBoxActive = useSelector(getIsSearchBoxActive);
  const showRoomsLabel = useSelector(getShowRoomsLabel);
  const numberOfGuests = useSelector(getNumberOfGuests);
  const hotelIdsForPriceColorCalendar = useSelector(getHotelIdsForPriceColorGuideCalendars);
  const searchParameters = useSelector(getSearchParameters);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const {
    includeLocalTaxes,
    includeTaxes
  } = useSelector(getMeta);
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const pricingContext = {
    includeTaxes,
    includeLocalTaxes,
    includeRoomsInNightlyPrice: isMultiRoomBundlesSearch
  };
  const {
    hotelId
  } = useSelector(getSearchBoxValues);
  const searchBoxSearchType = useSelector(getSearchBoxSearchType);
  const anchorComplete = useSelector(getAnchorComplete);

  // Added as part of ab519a8e-date-confirmation-tooltip
  const isUserLandingDirectlyOnSRP = useSelector(getIsUserLandingDirectlyOnSrp);
  const page = useSelector(getAnalyticsPageName);
  const isUserSearch = useSelector(getIsUserSearch);
  const isDefaultDates = useSelector(isDatesDefault);
  // End as part of ab519a8e-date-confirmation-tooltip

  const {
    brandIsKiwi,
    brandHasAlternativeSearchForm
  } = useBrand();
  const {
    datePickerReadyTimerStart,
    datePickerReadyTimerEnd,
    datePickerLoadedTimerStart,
    datePickerLoadedTimerEnd
  } = useDatePickerTiming();
  const containerRef = useRef<HTMLDivElement>();
  const guestPickerRef = useRef<HTMLDivElement>();
  const destinationInputRef = useRef<HTMLDivElement>();
  const [openedFormControl, setOpenedFormControl] = useState<OpenedFormControl>(OpenedFormControl.NONE);
  const openedDatePickerType = useSelector(getOpenDatePickerType);
  const {
    monthNames,
    weekDayNamesShort,
    firstDayOfWeek
  } = useContext(ConfigureI18nContext);
  const handleShowSearchBoxShadow = useCallback(() => {
    dispatch(setIsSearchBoxActive(true));
  }, [dispatch]);
  const handleHideSearchBoxShadow = useCallback(() => {
    dispatch(setIsSearchBoxActive(false));
  }, [dispatch]);
  const hasSomeAvailabilityPrices = useSelector(getHasSomeAvailabilityPrices);
  const {
    requestOneMonthAvailability,
    requestTwoMonthsAvailability
  } = useColorPriceCalendar({
    searchParameters,
    pricingContext
  });
  const isColorPriceDatePickerEnabled = numberOfGuests <= 2 && hasSomeAvailabilityPrices;
  useEffect(() => {
    // open the date picker if the openDatePickerType state changes
    if (openedDatePickerType === DatePickerType.CheckIn) {
      setOpenedFormControl(OpenedFormControl.CHECK_IN);
      handleShowSearchBoxShadow();
    }
    if (openedDatePickerType === DatePickerType.CheckOut) {
      setOpenedFormControl(OpenedFormControl.CHECK_OUT);
    }
  }, [openedDatePickerType, handleShowSearchBoxShadow]);
  const handleCloseSearchBox = () => {
    setOpenedFormControl(OpenedFormControl.NONE);
  };
  const handleSubmit = () => {
    handleHideSearchBoxShadow();
    onSubmit();
  };
  const handleSearchButtonClick = () => {
    handleSubmit();
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.SearchBoxSearchButton,
      action: Action.Clicked
    }));
  };
  const handleDestinationClick = () => {
    setOpenedFormControl(OpenedFormControl.DESTINATION);
    handleShowSearchBoxShadow();
    onDestinationPickerInputClick();
  };
  const handleDestinationChange = (arg0: OnDestinationChangeParams) => {
    onDestinationChange(arg0);
    setOpenedFormControl(OpenedFormControl.CHECK_IN);
  };
  const handleDatePickerSubmit = () => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.DatePickerSubmitButton,
      action: Action.Clicked
    }));
    handleCloseSearchBox();
    onDatePickerClose();
    handleSubmit();
  };
  const handleDatePickerOpen = (type: DatePickerType) => {
    onDatePickerOpen(type);
  };
  const handleDatePickerDayClick = (type: DatePickerType, value: string) => {
    datePickerReadyTimerStart();
    onDatePickerDayClick(type, value);
  };
  const handleCloseDestinationPicker = (e: MouseEvent) => {
    // do not close the destination picker if we are clicking within the input container
    if (!destinationInputRef?.current?.contains(e.target as Node)) {
      handleCloseSearchBox();
    }
  };
  const handleDatePickerDOMChange = useCallback(() => {
    datePickerReadyTimerEnd();
  }, [datePickerReadyTimerEnd]);
  const handleDatePickerMount = useCallback(() => {
    datePickerLoadedTimerEnd();
  }, [datePickerLoadedTimerEnd]);
  const handleCloseDatepicker = () => {
    onDatePickerClose();
    handleCloseSearchBox();
  };
  const handleDatePickerInputClick = useCallback((...args) => {
    // eslint-disable-next-line prefer-spread
    onDatePickerInputClick.apply(null, args);
    datePickerLoadedTimerStart();
  }, [datePickerLoadedTimerStart, onDatePickerInputClick]);
  const handleCheckInClick = () => {
    handleDatePickerInputClick('checkIn');
    onDatePickerOpen(DatePickerType.CheckIn);
    setOpenedFormControl(OpenedFormControl.CHECK_IN);
    handleShowSearchBoxShadow();
  };
  const handleCheckOutClick = () => {
    handleDatePickerInputClick('checkOut');
    onDatePickerOpen(DatePickerType.CheckOut);
    setOpenedFormControl(OpenedFormControl.CHECK_OUT);
    handleShowSearchBoxShadow();
  };
  const handleGuestsClick = () => {
    onGuestPickerClick();
    setOpenedFormControl(OpenedFormControl.GUEST);
    handleShowSearchBoxShadow();
  };
  const handleRecentSearchSelected = (params: RecentSearchParams) => {
    handleCloseSearchBox();
    handleHideSearchBoxShadow();
    onRecentSearchSelected(params);
  };
  const handleGuestPickerClose = () => {
    handleCloseSearchBox();
  };
  const handleGuestPickerSubmit = () => {
    onSubmit();
    handleHideSearchBoxShadow();
    handleCloseSearchBox();
  };
  const handleNearbyPropertiesSuggestionSelected = () => {
    setOpenedFormControl(OpenedFormControl.CHECK_IN);
    onNearbyPropertiesClick();
  };
  const handleOutsideClick = () => {
    if (isSearchBoxActive) {
      // Rely on the click outside to hide to shadow to avoid a flicker when moving from one input to another
      handleHideSearchBoxShadow();
      const checkInChanged = !isEqual(dateStringToMiddayDate(initialCheckIn), dateStringToMiddayDate(checkIn));
      const checkOutChanged = !isEqual(dateStringToMiddayDate(initialCheckOut), dateStringToMiddayDate(checkOut));
      const roomsSplitChanged = initialRooms !== roomsSplitToString(roomsSplit);
      const optimizeRoomsChanged = Boolean(initialOptimizeRooms) !== optimizeRooms;

      // Adding this so the reset only triggers if the user has interacted
      // with the date picker and guest picker.
      if (checkInChanged || checkOutChanged || roomsSplitChanged || optimizeRoomsChanged) {
        onDatePickerReset();
        onGuestPickerReset();
      }
    }
  };
  const isDestinationPickerOpen = openedFormControl === OpenedFormControl.DESTINATION;
  const isCheckInPickerOpen = openedFormControl === OpenedFormControl.CHECK_IN;
  const isCheckOutPickerOpen = openedFormControl === OpenedFormControl.CHECK_OUT;
  const isCheckInOrCheckOutPickerOpen = isCheckInPickerOpen || isCheckOutPickerOpen;
  const isGuestPickerOpen = openedFormControl === OpenedFormControl.GUEST;
  const getSearchFormComponent = () => {
    if (brandIsKiwi) {
      return KiwiSearchForm;
    }
    if (brandHasAlternativeSearchForm) {
      return SearchFormHorizontalFullWidth;
    }
    return SearchFormHorizontal;
  };
  const SearchFormComponent = getSearchFormComponent();
  useEffect(() => {
    // pre-fetch availability for the hotel searches
    if (searchBoxSearchType === SearchType.Hotel) {
      toggle('a8fj1jd3-color-price-calendar-2', () => null, () => {
        requestTwoMonthsAvailability(dateStringToMiddayDate(checkIn), [hotelId]);
      });
    }
  }, [hotelId]);
  useEffect(() => {
    // pre-fetch availability for the non-hotel searches
    if (searchBoxSearchType !== SearchType.Hotel && anchorComplete) {
      toggle('a8fj1jd3-color-price-calendar-2', () => null, () => {
        requestTwoMonthsAvailability(dateStringToMiddayDate(checkIn), hotelIdsForPriceColorCalendar);
      });
    }
  }, [anchorComplete, hotelIdsForPriceColorCalendar]);
  const dispatchTeamEvent = useDispatchTeamEvent();
  const trackEventCallback = useCallback((payload: TrackEventPayload) => dispatchTeamEvent(trackEvent(payload)), [dispatchTeamEvent]);

  // added for the loading experience - only show the searchbox once it contains values
  if (!destinationDisplayValue) return null;
  return <FormContainer ref={containerRef}>
      <OutsideClickHandler display="contents" onOutsideClick={handleOutsideClick}>
        <SearchFormComponent checkIn={checkIn} checkOut={checkOut} destinationButtonDisplayValue={destinationDisplayValue} destinationInputDisplayValue={destinationPickerInputDisplayValue} numberOfAdults={numberOfAdults} numberOfChildren={numberOfChildren} numberOfRooms={numberOfRooms} onDestinationPickerClick={handleDestinationClick} onCheckInDatePickerClick={handleCheckInClick} onCheckOutDatePickerClick={handleCheckOutClick} onGuestPickerClick={handleGuestsClick} onSubmit={handleSearchButtonClick} isDestinationPickerOpen={isDestinationPickerOpen} isCheckInPickerOpen={isCheckInPickerOpen} isCheckOutPickerOpen={isCheckOutPickerOpen} isGuestPickerOpen={isGuestPickerOpen} guestPickerRef={guestPickerRef} destinationInputRef={destinationInputRef} onSuggestionsRequested={onSuggestionsRequested} isRoomsLabelShown={showRoomsLabel} destinationHasError={false}
      // Added as part of ab519a8e-date-confirmation-tooltip
      isUserLandingDirectlyOnSRP={isUserLandingDirectlyOnSRP && page === Page.Search && !isUserSearch && isDefaultDates && toggle('ab519a8e-date-confirmation-tooltip', false, true)} trackEvent={trackEventCallback} />
        <DestinationPickerPopover isOpen={isDestinationPickerOpen} anchorRef={containerRef} onChange={handleDestinationChange} onSuggestionSelected={onSuggestionSelected} onRecentSearchSelected={handleRecentSearchSelected} onClose={handleCloseDestinationPicker} onNearbyPropertiesSuggestionSelected={handleNearbyPropertiesSuggestionSelected} onForceClosePopover={handleCloseSearchBox} />
        <DatePickerPopover isOpen={isCheckInOrCheckOutPickerOpen} openedDatePickerType={openedDatePickerType} anchorRef={containerRef} checkIn={checkIn} checkOut={checkOut} onMount={handleDatePickerMount} onClose={handleCloseDatepicker} onChange={onDateChange} onDatePickerOpen={handleDatePickerOpen} onDatePickerDayClick={handleDatePickerDayClick} onDOMChange={handleDatePickerDOMChange} onSubmit={handleDatePickerSubmit} verticalOffset={POPOVER_VERTICAL_OFFSET} horizontalOffset={POPOVER_HORIZONTAL_OFFSET} weekdaysShort={weekDayNamesShort} months={monthNames} firstDayOfWeek={firstDayOfWeek} showTotalPrices={showTotalPrices} shouldShowPrices={searchBoxSearchType === SearchType.Hotel} onMonthView={date => requestOneMonthAvailability(date, hotelIdsForPriceColorCalendar)} isColorPriceDatePickerEnabled={isColorPriceDatePickerEnabled} DayInnerContainer={WithColorPriceDayData} />
        <GuestRoomConfigPopover isOpen={isGuestPickerOpen} anchorRef={guestPickerRef} placement={PopoverPlacement.Left} roomsSplit={roomsSplit} onRoomsChange={onGuestRoomsChange} onOptimizeRoomsChange={onGuestOptimizeRoomsChange} onClose={handleGuestPickerClose} onSubmit={handleGuestPickerSubmit} onInfoOpen={onGuestPickerInfoDisplayed} onGuestConfigOptionClick={onGuestConfigPreferenceClick} verticalOffset={POPOVER_VERTICAL_OFFSET} horizontalOffset={POPOVER_HORIZONTAL_OFFSET} roomConfigType={optimizeRooms ? RoomConfigType.Optimized : RoomConfigType.Manual} submitButtonMessage={<FormattedMessageWrapper id="search" defaultMessage="Search" />} />
      </OutsideClickHandler>
    </FormContainer>;
};

require("./SearchFormDesktop.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchFormDesktop.tsx");